import CryptoJS from 'crypto-js';

// AES加密配置
const AES_CONFIG = {
  // 基础密钥（实际项目中应该从环境变量或配置文件获取）
  BASE_KEY: 'jianyin_admin_2025_secret_key',
  // 密钥长度
  KEY_SIZE: 256,
  // 加密模式
  MODE: CryptoJS.mode.CBC as any,
  // 填充方式
  PADDING: CryptoJS.pad.Pkcs7 as any,
  // IV长度
  IV_SIZE: 16,
};

/**
 * 获取AES密钥（使用固定基础密钥）
 * @param baseKey 基础密钥
 * @returns 固定的密钥
 */
export const getAESKey = (baseKey: string = AES_CONFIG.BASE_KEY): string => {
  // 直接使用基础密钥，不再基于时间戳生成动态密钥
  return baseKey;
};

/**
 * 生成随机IV
 * @returns 随机IV字符串
 */
export const generateIV = (): string => {
  return CryptoJS.lib.WordArray.random(AES_CONFIG.IV_SIZE).toString();
};

/**
 * AES加密
 * @param text 要加密的文本
 * @param key 加密密钥（可选，不传则使用默认密钥）
 * @param iv 初始化向量（可选，不传则生成随机IV）
 * @returns 加密结果对象 { encrypted: string, iv: string }
 */
export const aesEncrypt = (
  text: string,
  key?: string,
  iv?: string
): { encrypted: string; iv: string } => {
  try {
    // 使用固定密钥
    const aesKey = key || getAESKey();

    // 生成IV
    const aesIV = iv || generateIV();

    // 执行AES加密
    const encrypted = CryptoJS.AES.encrypt(text, aesKey, {
      iv: CryptoJS.enc.Utf8.parse(aesIV),
      mode: AES_CONFIG.MODE,
      padding: AES_CONFIG.PADDING,
    });

    return {
      encrypted: encrypted.toString(),
      iv: aesIV,
    };
  } catch (error) {
    console.error('AES加密失败:', error);
    throw new Error('密码加密失败');
  }
};

/**
 * AES解密
 * @param encrypted 加密的文本
 * @param key 解密密钥
 * @param iv 初始化向量
 * @returns 解密后的文本
 */
export const aesDecrypt = (encrypted: string, key: string, iv: string): string => {
  try {
    const decrypted = CryptoJS.AES.decrypt(encrypted, key, {
      iv: CryptoJS.enc.Utf8.parse(iv),
      mode: AES_CONFIG.MODE,
      padding: AES_CONFIG.PADDING,
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('AES解密失败:', error);
    throw new Error('密码解密失败');
  }
};

/**
 * 加密登录密码
 * @param password 原始密码
 * @returns 加密后的密码信息
 */
export const encryptLoginPassword = (password: string): {
  encryptedPassword: string;
  iv: string;
} => {
  // 使用固定密钥，IV动态生成
  const { encrypted, iv } = aesEncrypt(password);

  return {
    encryptedPassword: encrypted,
    iv,
  };
};

/**
 * 解密登录密码（后端使用）
 * @param encryptedPassword 加密的密码
 * @param iv 初始化向量
 * @returns 解密后的密码
 */
export const decryptLoginPassword = (
  encryptedPassword: string,
  iv: string
): string => {
  // 使用固定密钥
  const key = getAESKey(AES_CONFIG.BASE_KEY);
  return aesDecrypt(encryptedPassword, key, iv);
};

/**
 * 生成密码哈希（用于密码验证）
 * @param password 原始密码
 * @param salt 盐值（可选）
 * @returns 哈希值
 */
export const generatePasswordHash = (password: string, salt?: string): string => {
  const saltValue = salt || CryptoJS.lib.WordArray.random(16).toString();
  const hash = CryptoJS.PBKDF2(password, saltValue, {
    keySize: 256 / 32,
    iterations: 10000,
  });
  return hash.toString();
};

/**
 * 验证密码
 * @param password 原始密码
 * @param hash 存储的哈希值
 * @param salt 盐值
 * @returns 是否匹配
 */
export const verifyPassword = (password: string, hash: string, salt: string): boolean => {
  try {
    const computedHash = generatePasswordHash(password, salt);
    return computedHash === hash;
  } catch (error) {
    console.error('密码验证失败:', error);
    return false;
  }
};

// 导出配置（供其他模块使用）
export { AES_CONFIG };
